import { useRef, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { gsap } from '@shared/lib/gsap'
import { getAllPosts, getCategoryTranslation, categorySlug, POST_LOCALES, type PostMeta } from '@shared/lib/posts'
import { localeCategoryRoute, localeRoot, postLocaleFromPath, POST_LOCALE_LABELS } from '@shared/lib/post-locale'
import { useSeo } from '@shared/lib/seo'
import { SITE } from '@shared/config'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import * as styles from './BlogPage.css'

// How many cards to reveal per batch. The first paint only mounts this many,
// keeping initial DOM + image requests small; more load as the reader scrolls.
const PAGE_SIZE = 12

function formatDate(dateStr: string, locale: 'en' | 'fr' | 'es'): string {
  return new Date(dateStr).toLocaleDateString(POST_LOCALE_LABELS[locale].htmlLang, { year: 'numeric', month: 'long', day: 'numeric' })
}

export function BlogPage() {
  const { category } = useParams<{ category?: string }>()
  const location = useLocation()
  const locale = postLocaleFromPath(location.pathname)
  const labels = POST_LOCALE_LABELS[locale]
  const gridRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const allPosts = getAllPosts(locale)
  const categories = Array.from(new Set(allPosts.map((p) => p.category).filter(Boolean)))
  const activeCategoryName = category
    ? categories.find((c) => categorySlug(c) === category)
    : undefined

  const filtered = activeCategoryName
    ? allPosts.filter((p) => p.category === activeCategoryName)
    : allPosts

  // Home pages always cross-link every locale; category pages cross-link the
  // locales where a translated category exists, with x-default on the English
  // side — the same sets the prerender emits.
  const seoAlternates = useMemo(() => {
    if (!activeCategoryName) {
      return [
        ...POST_LOCALES.map((candidate) => ({ lang: POST_LOCALE_LABELS[candidate].htmlLang, path: localeRoot(candidate) })),
        { lang: 'x-default', path: '/' },
      ]
    }
    const members = POST_LOCALES.flatMap((candidate) => {
      const translated = getCategoryTranslation(activeCategoryName, locale, candidate)
      return translated
        ? [{ lang: POST_LOCALE_LABELS[candidate].htmlLang, path: localeCategoryRoute(candidate, categorySlug(translated)) }]
        : []
    })
    if (members.length < 2) return []
    const english = getCategoryTranslation(activeCategoryName, locale, 'en')
    return [
      ...members,
      ...(english ? [{ lang: 'x-default', path: localeCategoryRoute('en', categorySlug(english)) }] : []),
    ]
  }, [activeCategoryName, locale])

  useSeo({
    title: activeCategoryName ? `${activeCategoryName} | ${SITE.name}` : `${SITE.name} — ${labels.tagline}`,
    description: labels.description,
    path: activeCategoryName ? localeCategoryRoute(locale, category!) : localeRoot(locale),
    image: filtered[0]?.thumbnail,
    type: 'website',
    lang: labels.htmlLang,
    alternates: seoAlternates,
  })

  const featured = filtered[0]
  const rest = filtered.slice(1)

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const visibleRest = rest.slice(0, visibleCount)
  const hasMore = visibleCount < rest.length

  // Reset the reveal window whenever the category filter changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [category])

  // Reveal the next batch when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, rest.length))
        }
      },
      { rootMargin: '600px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, rest.length])

  useEffect(() => {
    if (!gridRef.current) return
    const ctx = gsap.context(() => {
      gsap.from(Array.from(gridRef.current!.children), {
        opacity: 0, y: 30, duration: 0.5, stagger: 0.06, ease: 'power3.out', clearProps: 'all',
      })
    }, gridRef)
    return () => ctx.revert()
  }, [category])

  return (
    <div className={styles.page}>
      <Header />

      <div className={styles.hero}>
        <p className={styles.heroLabel}>{labels.tagline}</p>
        <h1 className={styles.heroTitle}>{activeCategoryName ?? SITE.name}</h1>
        <p className={styles.heroDesc}>{labels.description}</p>
      </div>

      <div className={styles.body}>
        <div className={styles.filterBar}>
          <Link
            to={localeRoot(locale)}
            className={`${styles.filterBtn}${!activeCategoryName ? ` ${styles.filterBtnActive}` : ''}`}
            aria-current={!activeCategoryName ? 'page' : undefined}
          >
            {labels.all}
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat}
              to={localeCategoryRoute(locale, categorySlug(cat))}
              className={`${styles.filterBtn}${activeCategoryName === cat ? ` ${styles.filterBtnActive}` : ''}`}
              aria-current={activeCategoryName === cat ? 'page' : undefined}
            >
              {cat}
            </Link>
          ))}
        </div>

        {filtered.length === 0 && <div className={styles.empty}>{labels.empty}</div>}

        {featured && (
          <Link className={styles.featured} to={featured.route}>
            <div className={styles.featuredImgWrap}>
              <img src={featured.thumbnail} alt={featured.title} className={styles.featuredImg} />
            </div>
            <div className={styles.featuredBody}>
              <span className={styles.featuredCategory}>{featured.category}</span>
              <h2 className={styles.featuredTitle}>{featured.title}</h2>
              <p className={styles.featuredDesc}>{featured.description}</p>
              <div className={styles.featuredMeta}>
                <span>{featured.author}</span>
                <span className={styles.metaDot} />
                <span>{formatDate(featured.date, locale)}</span>
              </div>
            </div>
          </Link>
        )}

        {visibleRest.length > 0 && (
          <div className={styles.grid} ref={gridRef}>
            {visibleRest.map((post) => (
              <PostCard key={post.slug} post={post} locale={locale} />
            ))}
          </div>
        )}

        {hasMore && (
          <>
            <div className={styles.loadingMore}>{labels.loadingMore}</div>
            <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
          </>
        )}
      </div>
      <Footer />
    </div>
  )
}

function PostCard({ post, locale }: { post: PostMeta; locale: 'en' | 'fr' | 'es' }) {
  return (
    <Link className={styles.card} to={post.route}>
      <div className={styles.cardImgWrap}>
        <img src={post.thumbnail} alt={post.title} className={styles.cardImg} loading="lazy" decoding="async" />
      </div>
      <div className={styles.cardBody}>
        <span className={styles.cardCategory}>{post.category}</span>
        <h3 className={styles.cardTitle}>{post.title}</h3>
        <p className={styles.cardDesc}>{post.description}</p>
        <div className={styles.cardMeta}>
          <span>{post.author}</span>
          <span className={styles.metaDot} />
          <span>{formatDate(post.date, locale)}</span>
        </div>
      </div>
    </Link>
  )
}
